/**
 * AP-34 21. — **Eingangsrechnungen** im Listen-Standard (FilterBar, Spalten, Sortierung, Seiten).
 * Gefiltert, sortiert und paginiert am Server; ein Klick öffnet die Erfassung.
 *
 * Mit `stapel` zeigt dieselbe Seite den **Eingangsstapel**: hochgeladene Dateien ohne Lieferant,
 * dazu die Ablagefläche.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Plus, Settings, Sparkles, Upload } from 'lucide-react'
import { INCOMING_INVOICE_STATUSES, erfuelltRecht, geldText, type IncomingInvoiceStatus } from '@symphenia/shared-types'
import { FilterBar, filterAusAdresse } from '@/components/list/FilterBar'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { pageWide } from '@/lib/form-styles'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { useTerminology } from '@/lib/useTerminology'
import { ExportButton } from '@/components/ui/ExportButton'
import { fetchIncomingFrame, fetchIncomingInvoices, fetchOpenItemsSummary, fetchOpenRetentions, incomingExportRequest, startStackReading, uploadIncomingInvoices } from '../api/incoming-invoices.api'
import { CreateInvoiceDialog } from '../components/CreateInvoiceDialog'
import { StapelLesung, istZuLesen, liestNoch } from '../components/StapelLesung'
import { ART_TEXT, STATUS_TEXT, kostenzielText, tagText } from '../lib/texte'
import type { IncomingFrame, IncomingInvoiceRow, ListIncomingParams, OpenItemsSummary, OpenRetention, Paginated } from '../types/incoming-invoices.types'

interface ColDef {
  key: string
  label: string
  align?: 'right'
  sortable?: boolean
}

const spalten = (projekt: string, stapel = false, mitKi = false): ColDef[] => [
  // Nachtrag 4, PT-18 — nur im Eingangsstapel: welche Datei es ist und wie weit die KI mit ihr ist
  ...(stapel ? [{ key: 'fileName', label: 'Datei' }] : []),
  ...(stapel && mitKi ? [{ key: 'reading', label: 'KI-Lesung' }] : []),
  { key: 'supplierName', label: 'Lieferant', sortable: true },
  { key: 'supplierInvoiceNumber', label: 'Rechnungsnummer' },
  { key: 'kind', label: 'Art' },
  { key: 'invoiceDate', label: 'Rechnungsdatum', sortable: true },
  { key: 'receivedDate', label: 'Eingang', sortable: true },
  { key: 'grossTotal', label: 'Brutto', align: 'right', sortable: true },
  { key: 'netTotal', label: 'Netto', align: 'right' },
  { key: 'dueDate', label: 'Fällig', sortable: true },
  { key: 'discountDueDate', label: 'Skonto bis', sortable: true },
  { key: 'discountAmount', label: 'Skonto', align: 'right' },
  { key: 'openAmount', label: 'offen', align: 'right', sortable: true },
  { key: 'paidAmount', label: 'bezahlt', align: 'right' },
  { key: 'paymentState', label: 'Zahlung' },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'costTargets', label: `Kostenziel (${projekt}, Lager, Gemeinkosten)` },
  { key: 'createdAt', label: 'angelegt am', sortable: true },
]
const DEFAULT_COLUMN_KEYS = ['supplierName', 'supplierInvoiceNumber', 'invoiceDate', 'grossTotal', 'dueDate', 'status', 'costTargets']
const STAPEL_COLUMN_KEYS = ['fileName', 'reading', 'receivedDate', 'createdAt']

/** Die Fehler der KI tragen (wie in der Lesung) keinen Text in den Sprachdateien. */
const KI_FEHLER: Record<string, string> = {
  INCOMING_INVOICE_AI_CONSENT: 'Für die KI-Lesung fehlt die Einwilligung — sie steht in den Einstellungen der Eingangsrechnungen.',
  INCOMING_INVOICE_AI_OFF: 'Die KI ist für deinen Betrieb nicht eingeschaltet (Einstellungen → KI).',
}
/** Nach dem Ablegen: was im Eingangsstapel liegt — wie weit die KI ist, sagt die Spalte „KI-Lesung". */
const abgelegtText = (n: number) => `${n === 1 ? 'Eine Rechnung liegt' : `${n} Rechnungen liegen`} im Eingangsstapel. Ein Klick auf die Zeile öffnet die Erfassung.`
const OFFEN_COLUMN_KEYS = ['supplierName', 'supplierInvoiceNumber', 'invoiceDate', 'grossTotal', 'openAmount', 'dueDate', 'discountDueDate', 'discountAmount', 'paymentState', 'status']

const ZAHLSTATUS_TEXT: Record<string, { label: string; klasse: string }> = {
  NONE: { label: 'noch nicht freigegeben', klasse: 'text-muted-foreground' },
  OPEN: { label: 'offen', klasse: '' },
  PARTIALLY_PAID: { label: 'teilbezahlt', klasse: 'text-sky-700 dark:text-sky-300' },
  OVERDUE: { label: 'überfällig', klasse: 'font-medium text-destructive' },
  PAID: { label: 'bezahlt', klasse: 'text-emerald-700 dark:text-emerald-300' },
}

const filterFelder = (rahmen: IncomingFrame | null, projekt: string, stapel: boolean): FilterFieldDef[] => [
  { key: 'search', label: 'Suche', type: 'text', category: 'core', operators: ['contains'] },
  ...(stapel
    ? []
    : ([
        {
          key: 'status',
          label: 'Status',
          type: 'select-multi',
          category: 'core',
          operators: ['is'],
          options: INCOMING_INVOICE_STATUSES.filter((s) => s !== 'UNCAPTURED').map((s) => ({ value: s, label: STATUS_TEXT[s].label })),
        },
        { key: 'from', label: 'Rechnungsdatum ab', type: 'date', category: 'core', operators: ['is'] },
        { key: 'to', label: 'Rechnungsdatum bis', type: 'date', category: 'core', operators: ['is'] },
        {
          key: 'costTarget',
          label: 'Kostenziel',
          type: 'select',
          category: 'core',
          operators: ['is'],
          options: (['PROJECT', 'STOCK', 'OVERHEAD'] as const).map((z) => ({ value: z, label: kostenzielText(z, projekt) })),
        },
        {
          key: 'overheadCategoryId',
          label: 'Gemeinkosten-Kategorie',
          type: 'select',
          category: 'core',
          operators: ['is'],
          options: (rahmen?.overheadCategories ?? []).map((k) => ({ value: k.id, label: k.name })),
        },
      ] as FilterFieldDef[])),
  ...(stapel
    ? []
    : ([
        { key: 'dueFrom', label: 'Fällig ab', type: 'date', category: 'core', operators: ['is'] },
        { key: 'dueUntil', label: 'Fällig bis', type: 'date', category: 'core', operators: ['is'] },
        { key: 'discountFrom', label: 'Skonto bis — ab', type: 'date', category: 'core', operators: ['is'] },
        { key: 'discountUntil', label: 'Skonto bis — bis', type: 'date', category: 'core', operators: ['is'] },
      ] as FilterFieldDef[])),
  { key: 'mine', label: 'nur meine', type: 'boolean', category: 'core', operators: ['is'] },
  { key: 'blocked', label: 'nur gesperrte', type: 'boolean', category: 'core', operators: ['is'] },
]

function filterParams(filters: Record<string, ActiveFilter>): ListIncomingParams {
  const p: ListIncomingParams = {}
  for (const [key, a] of Object.entries(filters)) {
    if (!a.value) continue
    if (key === 'search') p.search = a.value
    if (key === 'status') p.status = a.value
    if (key === 'from') p.from = a.value.slice(0, 10)
    if (key === 'to') p.to = a.value.slice(0, 10)
    if (key === 'dueFrom' || key === 'dueUntil' || key === 'discountFrom' || key === 'discountUntil') p[key] = a.value.slice(0, 10)
    if (key === 'costTarget') p.costTarget = a.value
    if (key === 'overheadCategoryId') p.overheadCategoryId = a.value
    if (key === 'mine' && a.value === 'true') p.mine = true
    if (key === 'blocked' && a.value === 'true') p.blocked = true
  }
  return p
}

interface ColumnState {
  columnKeys: string[]
  columnWidths: Record<string, number>
  sortKey: string
  sortDir: 'asc' | 'desc'
}

const TOEPFE: [string, string][] = [
  ['NOT_DUE', 'nicht fällig'],
  ['D1_30', '1–30 Tage überfällig'],
  ['D31_60', '31–60 Tage'],
  ['OVER_60', 'über 60 Tage'],
]

/** 12.1 — Summe und Alterssummen der offenen Posten; lädt neu, wenn sich die Liste ändert. */
function OffenePostenKopf({ stand }: { stand: unknown }) {
  const [u, setU] = useState<OpenItemsSummary | null>(null)
  useEffect(() => {
    let aus = false
    fetchOpenItemsSummary()
      .then((x) => !aus && setU(x))
      .catch(() => !aus && setU(null))
    return () => {
      aus = true
    }
  }, [stand])
  if (!u) return null
  const kachel = 'rounded-lg border border-border bg-card px-4 py-2'
  return (
    <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
      <div className={kachel}>
        <p className="text-xs text-muted-foreground">offen gesamt · {u.count} Rechnungen</p>
        <p className="text-lg font-semibold tabular-nums">{geldText(u.total)}</p>
      </div>
      {TOEPFE.map(([k, label]) => (
        <div key={k} className={kachel}>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className={`text-lg tabular-nums ${k !== 'NOT_DUE' && u.buckets[k] ? 'font-semibold text-destructive' : ''}`}>{geldText(u.buckets[k] ?? 0)}</p>
        </div>
      ))}
      <div className={kachel}>
        <p className="text-xs text-muted-foreground">Skonto noch erreichbar</p>
        <p className="text-lg tabular-nums">{geldText(u.discountAvailable)}</p>
        {(u.blocked > 0 || u.notReleased > 0) && <p className="text-xs text-muted-foreground">{[u.blocked ? `${u.blocked} gesperrt` : '', u.notReleased ? `${u.notReleased} nicht freigegeben` : ''].filter(Boolean).join(' · ')}</p>}
      </div>
    </div>
  )
}

/** 12.1 — Rücklässe, die noch einbehalten sind: fällig am, durch Bankgarantie abgelöst. Erscheint nur, wenn es welche gibt. */
function OffeneRuecklaesse() {
  const navigate = useNavigate()
  const [zeilen, setZeilen] = useState<OpenRetention[]>([])
  useEffect(() => {
    let aus = false
    fetchOpenRetentions()
      .then((z) => !aus && setZeilen(z))
      .catch(() => !aus && setZeilen([]))
    return () => {
      aus = true
    }
  }, [])
  if (!zeilen.length) return null
  const summe = zeilen.filter((z) => !z.replacedByGuarantee).reduce((s, z) => s + Math.round(z.amount * 100), 0) / 100
  return (
    <div className="rounded-lg border border-border bg-card shadow-sm">
      <p className="border-b border-border bg-muted/50 px-4 py-2 text-sm font-medium">
        Rücklässe — einbehalten {geldText(summe)}
      </p>
      <ul className="divide-y divide-border text-sm">
        {zeilen.map((z) => (
          <li key={z.id}>
            <button type="button" className="flex w-full flex-wrap items-center gap-x-4 gap-y-0.5 px-4 py-1.5 text-left hover:bg-accent/30" onClick={() => navigate(`/incoming-invoices/${z.invoiceId}`)}>
              <span className="tabular-nums">{z.invoiceNumber}</span>
              <span>{z.supplierName}</span>
              <span className="text-muted-foreground">{z.kind === 'WARRANTY_RETENTION' ? 'Haftrücklass' : 'Deckungsrücklass'}</span>
              {z.releaseDate && <span className="text-muted-foreground">fällig {tagText(z.releaseDate)}</span>}
              {z.replacedByGuarantee && <span className="rounded bg-sky-500/15 px-1.5 text-xs text-sky-700 dark:text-sky-300">Bankgarantie{z.guaranteeValidUntil ? ` bis ${tagText(z.guaranteeValidUntil)}` : ''}</span>}
              <span className="ml-auto font-medium tabular-nums">{geldText(z.amount)}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

function StatusPill({ status }: { status: IncomingInvoiceStatus }) {
  const s = STATUS_TEXT[status]
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${s.klasse}`}>{s.label}</span>
}

type Ansicht = { stapel?: boolean; freigaben?: boolean; offen?: boolean }

/**
 * Vier Ansichten, eine Seite — jede mit **eigenem** Zustand. Der Router setzt beim Wechsel zwischen ihnen dieselbe
 * Komponente an dieselbe Stelle, und ohne eigenen Schlüssel behielte sie Spalten, Sortierung, Filter und Seite der
 * Ansicht, aus der man kommt: Der Eingangsstapel zeigte nach „Rechnungen" deren Spalten statt „Datei" und „KI-Lesung",
 * bis jemand neu lud — und eine Spaltenänderung in diesem Zustand überschrieb das Layout der falschen Ansicht
 * (aufgefallen am 10.10.2026, als abgelegte Rechnungen aus der Liste in den Stapel führten; Nachtrag 6, PT-22).
 */
export default function IncomingInvoicesPage(ansicht: Ansicht) {
  return <Ansichtsseite key={ansicht.stapel ? 'stapel' : ansicht.freigaben ? 'freigaben' : ansicht.offen ? 'offen' : 'liste'} {...ansicht} />
}

function Ansichtsseite({ stapel = false, freigaben = false, offen = false }: Ansicht) {
  const navigate = useNavigate()
  const terms = useTerminology()
  const { user } = useAuthStore()
  const darfAnlegen = erfuelltRecht('incoming_invoices.invoice.create', user?.permissions)
  const darfLesen = erfuelltRecht('incoming_invoices.invoice.update', user?.permissions)
  // Der Stapel hat seit Nachtrag 4 eigene Spalten (Datei, KI-Lesung) — ein neuer Schlüssel, damit sie auch erscheinen, wo ein altes Layout gespeichert ist.
  const layoutKey = stapel ? 'columnbar_layout_incoming_inbox2' : freigaben ? 'columnbar_layout_incoming_approvals' : offen ? 'columnbar_layout_incoming_open_items' : 'columnbar_layout_incoming_invoices'
  const vorgabeSpalten = stapel ? STAPEL_COLUMN_KEYS : offen ? OFFEN_COLUMN_KEYS : DEFAULT_COLUMN_KEYS
  const [rahmen, setRahmen] = useState<IncomingFrame | null>(null)
  useEffect(() => {
    fetchIncomingFrame().then(setRahmen).catch(() => setRahmen(null))
  }, [])
  const mitKi = !!rahmen?.aiCapture
  const alleSpalten = useMemo(() => spalten(terms.project, stapel, mitKi), [terms.project, stapel, mitKi])
  const filterFields = useMemo(() => filterFelder(rahmen, terms.project, stapel), [rahmen, terms.project, stapel])
  // AP-40 6.4: Filter aus der Adresse (Kachel des Dashboards) — einmal beim Öffnen gelesen.
  const [suchParameter] = useSearchParams()
  const [adresse] = useState(() => filterAusAdresse(suchParameter, filterFelder(null, '', stapel)))

  const vorgabe = (): ColumnState => ({ columnKeys: vorgabeSpalten, columnWidths: {}, sortKey: 'createdAt', sortDir: 'desc' })
  const ladeSpalten = (): ColumnState => {
    const g = loadUiSetting<Partial<ColumnState> | null>(layoutKey, null)
    return g?.columnKeys?.length ? { columnKeys: g.columnKeys, columnWidths: g.columnWidths ?? {}, sortKey: g.sortKey ?? 'createdAt', sortDir: g.sortDir ?? 'desc' } : vorgabe()
  }

  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})
  const [colState, setColState] = useState<ColumnState>(ladeSpalten)
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [result, setResult] = useState<Paginated<IncomingInvoiceRow> | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [anlegen, setAnlegen] = useState(false)
  const [zieht, setZieht] = useState(false)
  const [laedtHoch, setLaedtHoch] = useState(false)
  const [startet, setStartet] = useState(false)
  const [hinweis, setHinweis] = useState<string | null>(null)
  // Der Satz nach dem Ablegen — ohne die Farbe der KI: Abgelegt ist auch, was niemand liest.
  const [abgelegt, setAbgelegt] = useState<string | null>(null)
  // Von der Rechnungsliste aus abgelegt: Der Hinweis kommt mit der Adresse in den Stapel — und steht nach dem
  // Neuladen der Seite nicht noch einmal da.
  const ort = useLocation()
  useEffect(() => {
    const n = (ort.state as { abgelegt?: number } | null)?.abgelegt
    if (!stapel || !n) return
    setAbgelegt(abgelegtText(n))
    navigate(ort.pathname + ort.search, { replace: true, state: null })
  }, [stapel, ort, navigate])
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dragKeyRef = useRef<string | null>(null)
  const dateiWahl = useRef<HTMLInputElement>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)

  const fetchData = useCallback(
    // `leise`: das Nachfragen, solange die KI liest — ohne „Laden…" und ohne eine Fehlermeldung zu löschen oder zu setzen
    async (filters: Record<string, ActiveFilter>, seite: number, state: ColumnState, leise = false) => {
      if (!leise) {
        setLoading(true)
        setError(null)
      }
      try {
        const p = filterParams(filters)
        // Der Eingangsstapel ist genau der Stand „im Eingang"; die Liste zeigt ihn nicht.
        const status = stapel ? 'UNCAPTURED' : (p.status ?? INCOMING_INVOICE_STATUSES.filter((s) => s !== 'UNCAPTURED').join(','))
        // „Meine Freigaben" (11.5): Rechnungen im Lauf, an denen ich eine offene Aufgabe habe
        setResult(await fetchIncomingInvoices({ ...p, ...(freigaben ? { myApprovals: true, status: undefined } : offen ? { openItems: true, status: p.status } : { status }), ...(stapel && { withReading: true }), page: seite, limit: 50, sortBy: state.sortKey, sortDir: state.sortDir }))
      } catch (e) {
        if (!leise) setError(apiErrorText(e, 'Die Eingangsrechnungen ließen sich nicht laden.'))
      } finally {
        if (!leise) setLoading(false)
      }
    },
    [stapel, freigaben, offen],
  )

  useEffect(() => {
    void fetchData(filterValues, page, colState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, stapel, freigaben, offen])

  const handleFiltersChange = useCallback(
    (neu: Record<string, ActiveFilter>) => {
      setFilterValues(neu)
      setPage(1)
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => void fetchData(neu, 1, colState), 400)
    },
    [fetchData, colState],
  )

  const updateColState = useCallback(
    (updater: (prev: ColumnState) => ColumnState) => {
      setColState((prev) => {
        const next = updater(prev)
        saveUiSetting(layoutKey, next)
        return next
      })
    },
    [layoutKey],
  )

  const handleSort = (key: string) => {
    const dir: 'asc' | 'desc' = colState.sortKey === key && colState.sortDir === 'asc' ? 'desc' : 'asc'
    const next = { ...colState, sortKey: key, sortDir: dir }
    updateColState(() => next)
    setPage(1)
    void fetchData(filterValues, 1, next)
  }

  const handleDrop = (ziel: string) => {
    const quelle = dragKeyRef.current
    setDragOverKey(null)
    if (!quelle || quelle === ziel) return
    updateColState((prev) => {
      const keys = [...prev.columnKeys]
      const von = keys.indexOf(quelle)
      const nach = keys.indexOf(ziel)
      if (von < 0 || nach < 0) return prev
      keys.splice(von, 1)
      keys.splice(nach, 0, quelle)
      return { ...prev, columnKeys: keys }
    })
  }

  const handleResizeStart = (e: React.MouseEvent, key: string) => {
    e.preventDefault()
    const th = (e.target as HTMLElement).closest('th')
    const startW = th?.getBoundingClientRect().width ?? 120
    const startX = e.clientX
    const onMove = (ev: MouseEvent) => {
      const w = Math.max(60, startW + ev.clientX - startX)
      updateColState((prev) => ({ ...prev, columnWidths: { ...prev.columnWidths, [key]: w } }))
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  // Nachtrag 4, PT-18: Solange die KI an einer Rechnung des Stapels liest, alle vier Sekunden nachsehen —
  // sonst blieben die Punkte stehen, bis jemand neu lädt (`.claude/rules/ki-beschaeftigungsanzeige.md`).
  const liest = stapel && (result?.data ?? []).some((z) => liestNoch(z.reading))
  const nachfragen = useRef<() => void>(() => undefined)
  nachfragen.current = () => void fetchData(filterValues, page, colState, true)
  useEffect(() => {
    if (!liest) return
    const t = setInterval(() => nachfragen.current(), 4000)
    return () => clearInterval(t)
  }, [liest])

  /** „Stapel mit KI lesen": alles im Eingangsstapel, das noch nicht gelesen ist — die Läufe arbeiten im Hintergrund. */
  const stapelLesen = async () => {
    setStartet(true)
    setError(null)
    setHinweis(null)
    setAbgelegt(null)
    try {
      const a = await startStackReading()
      const uebersprungen = a.skipped ? ` ${a.skipped === 1 ? 'Eine Datei kann' : `${a.skipped} Dateien können`} die KI nicht lesen (nur PDF und Bilder bis 25 MB).` : ''
      setHinweis(
        a.started
          ? `${a.started === 1 ? 'Eine Rechnung wird' : `${a.started} Rechnungen werden`} gelesen. Du musst nicht warten — die Vorschläge stehen danach an den Rechnungen.${uebersprungen}${a.more ? ' Der Stapel ist größer; starte danach noch einmal.' : ''}`
          : `Es gab nichts zu lesen.${uebersprungen}`,
      )
      await fetchData(filterValues, page, colState, true)
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code
      setError((code && KI_FEHLER[code]) ?? apiErrorText(e, 'Die Lesung ließ sich nicht starten.'))
    } finally {
      setStartet(false)
    }
  }

  /**
   * Nachtrag 6, PT-22: Abgelegte Rechnungen bleiben im Eingangsstapel — auch eine einzelne öffnet sich nicht mehr
   * von selbst. Wer zehn Rechnungen nacheinander auf die Seite zieht, stand sonst nach jeder in der Erfassung.
   * Gelesen wird im Hintergrund (Automatik der KI-Lesung); die Spalte „KI-Lesung" zeigt, wie weit sie ist.
   */
  const hochladen = async (dateien: File[]) => {
    if (!dateien.length || !darfAnlegen) return
    setLaedtHoch(true)
    setError(null)
    setHinweis(null)
    setAbgelegt(null)
    try {
      const neu = await uploadIncomingInvoices(dateien)
      if (!stapel) return navigate('/incoming-invoices/inbox', { state: { abgelegt: neu.length } })
      setAbgelegt(abgelegtText(neu.length))
      // Die neuen stehen auf der ersten Seite; leise, damit der Hinweis stehen bleibt und nichts zuckt.
      setPage(1)
      await fetchData(filterValues, 1, colState, true)
    } catch (e) {
      setError(apiErrorText(e, 'Die Dateien ließen sich nicht ablegen (erlaubt: PDF, Bild, XML bis 25 MB).'))
    } finally {
      setLaedtHoch(false)
    }
  }

  const shownColumns = colState.columnKeys.map((k) => alleSpalten.find((c) => c.key === k)).filter((c): c is ColDef => !!c)
  const availableToAdd = alleSpalten.filter((c) => !colState.columnKeys.includes(c.key))

  const heute = new Date().toISOString().slice(0, 10)
  const renderCell = (z: IncomingInvoiceRow, col: ColDef): React.ReactNode => {
    switch (col.key) {
      case 'fileName':
        return z.fileName ?? ''
      case 'reading':
        return <StapelLesung lesung={z.reading} />
      case 'supplierName':
        return z.supplierName ?? '—'
      case 'supplierInvoiceNumber':
        return z.supplierInvoiceNumber ?? ''
      case 'kind':
        return ART_TEXT[z.kind]
      case 'invoiceDate':
        return tagText(z.invoiceDate)
      case 'receivedDate':
        return tagText(z.receivedDate)
      case 'dueDate':
        return tagText(z.dueDate)
      case 'discountDueDate': {
        if (!z.discountDueDate) return ''
        // 12.1 — farbig: abgelaufen · innerhalb der Warntage · später
        const rest = Math.round((Date.parse(z.discountDueDate) - Date.parse(heute)) / 86_400_000)
        const farbe = !offen ? '' : rest < 0 ? 'text-muted-foreground line-through' : rest <= (rahmen?.discountWarningDays ?? 3) ? 'font-medium text-amber-700 dark:text-amber-300' : ''
        return <span className={farbe}>{tagText(z.discountDueDate)}</span>
      }
      case 'grossTotal':
        return geldText(z.statedGross ?? z.grossTotal)
      case 'netTotal':
        return geldText(z.statedNet ?? z.netTotal)
      case 'openAmount':
        return <span className={z.paymentStatus === 'NONE' ? 'text-muted-foreground' : 'font-medium'}>{geldText(z.openAmount)}</span>
      case 'paidAmount':
        return z.paidAmount ? geldText(z.paidAmount) : ''
      case 'discountAmount':
        // Skonto zählt nur, solange die Frist läuft
        return z.discountAmount && z.discountDueDate && z.discountDueDate >= heute ? geldText(z.discountAmount) : ''
      case 'paymentState':
        return z.paymentBlocked ? (
          <span className="rounded bg-destructive/15 px-1.5 py-0.5 text-xs text-destructive" title={z.paymentBlockedReason === 'IBAN_CHANGED' ? 'IBAN weicht vom Lieferanten ab' : (z.paymentBlockedReason ?? '')}>
            gesperrt
          </span>
        ) : (
          <span className={ZAHLSTATUS_TEXT[z.paymentStatus]?.klasse}>{ZAHLSTATUS_TEXT[z.paymentStatus]?.label ?? z.paymentStatus}</span>
        )
      case 'status':
        return <StatusPill status={z.status} />
      case 'costTargets':
        return (z.costTargets ?? []).map((k) => k.label ?? kostenzielText(k.target, terms.project)).join(', ')
      case 'createdAt':
        return tagText(z.createdAt)
      default:
        return null
    }
  }

  const rows = result?.data ?? []
  const meta = result?.meta
  const summe = rows.reduce((s, z) => s + (z.statedGross ?? z.grossTotal), 0)
  const summeOffen = rows.reduce((s, z) => s + Math.round(z.openAmount * 100), 0) / 100
  const totalCols = 1 + shownColumns.length + (columnConfigMode && availableToAdd.length ? 1 : 0) + 1
  const ungelesen = stapel && mitKi ? rows.filter(istZuLesen).length : 0
  // Auf der Seite stehen höchstens 50 — liegt mehr im Stapel, kann auch dahinter Ungelesenes sein.
  const mehrSeiten = (meta?.total ?? 0) > rows.length
  const titel = stapel ? 'Eingangsstapel' : freigaben ? 'Meine Freigaben' : offen ? 'Offene Posten' : 'Eingangsrechnungen'
  // Listen-Standard: die Liste als Datei — mit denselben Filtern und den sichtbaren Spalten; auch per E-Mail.
  const exportKnopf = (
    <ExportButton
      request={(format, orientation) => {
        const p = filterParams(filterValues)
        return incomingExportRequest(
          { ...p, ...(offen ? { openItems: true } : { status: p.status ?? INCOMING_INVOICE_STATUSES.filter((s) => s !== 'UNCAPTURED').join(',') }), sortBy: colState.sortKey, sortDir: colState.sortDir, format, orientation },
          [{ key: 'number', label: 'Eingangsnummer' }, ...shownColumns.map((c) => ({ key: c.key, label: c.label }))],
        )
      }}
      mail={{ source: 'EXPORT_INCOMING_INVOICES', title: titel, subject: titel }}
    />
  )
  // Nachtrag 4, PT-18 — nur mit dem Baustein, nur für den, der Rechnungen bearbeiten darf, und nur, solange es etwas zu lesen gibt
  const stapelKnopf = stapel && mitKi && darfLesen && (ungelesen > 0 || mehrSeiten) && (
    <button
      type="button"
      className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-violet-500/50 px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-500/10 disabled:opacity-50 dark:text-violet-300"
      disabled={startet}
      // Ein `title` allein würde den Namen des Knopfs für Vorlesehilfen ersetzen.
      aria-label="Stapel mit KI lesen"
      title="Startet die KI-Lesung für alle Rechnungen im Stapel, die noch nicht gelesen sind — sie läuft im Hintergrund"
      onClick={() => void stapelLesen()}
    >
      <Sparkles className="h-4 w-4" /> {startet ? 'Startet …' : `Stapel mit KI lesen${ungelesen && !mehrSeiten ? ` (${ungelesen})` : ''}`}
    </button>
  )
  const pfeil = (key: string) =>
    colState.sortKey === key ? colState.sortDir === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" /> : <ArrowUpDown className="h-3 w-3 opacity-30" />

  return (
    <div
      className={pageWide}
      onDragOver={(e) => {
        if (darfAnlegen && [...e.dataTransfer.types].includes('Files')) {
          e.preventDefault()
          setZieht(true)
        }
      }}
      onDragLeave={(e) => e.currentTarget === e.target && setZieht(false)}
      onDrop={(e) => {
        if (!zieht) return
        e.preventDefault()
        setZieht(false)
        void hochladen([...e.dataTransfer.files])
      }}
    >
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{titel}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {stapel
              ? mitKi
                ? 'Hochgeladene Rechnungen, die noch keinen Lieferanten haben. Die KI liest sie im Hintergrund — du musst nicht warten. Ein Klick öffnet die Erfassung mit ihrem Vorschlag; mit dem Lieferanten wird daraus ein Entwurf mit Eingangsnummer.'
                : 'Hochgeladene Rechnungen, die noch keinen Lieferanten haben. Ein Klick öffnet die Erfassung; mit dem Lieferanten wird daraus ein Entwurf mit Eingangsnummer.'
              : freigaben
                ? 'Rechnungen, die auf deine Freigabe warten. Ein Klick öffnet die Rechnung samt Original — dort gibst du frei oder lehnst ab.'
                : offen
                  ? 'Alles, was noch zu zahlen ist — auch Rechnungen, die noch nicht freigegeben sind, damit die Liste die ganze Verbindlichkeit zeigt.'
                  : 'Jede Lieferantenrechnung einmal erfasst — ein Klick öffnet sie. Dateien lassen sich auf die Seite ziehen; sie kommen in den Eingangsstapel.'}
          </p>
        </div>
        {offen && exportKnopf}
        {!stapel && !freigaben && !offen && !darfAnlegen && exportKnopf}
        {!darfAnlegen && stapelKnopf}
        {darfAnlegen && !freigaben && !offen && (
          // Die Knöpfe behalten ihre Breite — sonst bricht der lange Satz daneben ihre Beschriftung um.
          <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
            {!stapel && exportKnopf}
            {stapelKnopf}
            <input ref={dateiWahl} type="file" multiple accept="application/pdf,image/png,image/jpeg,image/webp,.xml" className="hidden" onChange={(e) => void hochladen([...(e.target.files ?? [])])} />
            <button type="button" className="inline-flex items-center gap-1 rounded-md border border-border px-4 py-2 text-sm text-foreground hover:bg-muted disabled:opacity-50" disabled={laedtHoch} onClick={() => dateiWahl.current?.click()}>
              <Upload className="h-4 w-4" /> {laedtHoch ? 'Lädt hoch …' : 'Hochladen'}
            </button>
            <button type="button" className="inline-flex items-center gap-1 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground" onClick={() => setAnlegen(true)}>
              <Plus className="h-4 w-4" /> Neue Rechnung
            </button>
          </div>
        )}
      </div>

      {zieht && <div className="rounded-lg border-2 border-dashed border-primary bg-primary/5 px-4 py-6 text-center text-sm text-primary">Dateien hier ablegen — je Datei entsteht eine Rechnung im Eingangsstapel.</div>}

      {abgelegt && (
        <p className="rounded-md bg-muted px-4 py-2 text-sm" role="status">
          {abgelegt}
        </p>
      )}
      {hinweis && (
        <p className="rounded-md bg-violet-500/10 px-4 py-2 text-sm" role="status">
          {hinweis}
        </p>
      )}

      {offen && <OffenePostenKopf stand={result} />}

      <FilterBar availableFields={filterFields} value={filterValues} onChange={handleFiltersChange} layoutKey={stapel ? 'incoming_inbox' : freigaben ? 'incoming_approvals' : offen ? 'incoming_open_items' : 'incoming_invoices'} defaultFieldKeys={stapel || freigaben ? ['search'] : offen ? ['search', 'blocked'] : ['search', 'status', 'costTarget']} defaultValues={{}} adresse={adresse} />

      <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${columnConfigMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
          title="Spalten konfigurieren"
        >
          {columnConfigMode ? <Check size={14} /> : <Settings size={14} />}
        </button>
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />

        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={layoutKey}
              currentValue={colState}
              onReset={(value) => {
                const next = (value as ColumnState | null) ?? vorgabe()
                markUiSettingApplied(layoutKey, next)
                setColState(next)
                void fetchData(filterValues, 1, next)
              }}
            />
          </div>
        )}

        {error && <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="relative px-4 py-1.5 text-left font-medium text-foreground" style={colState.columnWidths.number ? { width: colState.columnWidths.number } : undefined}>
                  <button type="button" onClick={() => handleSort('number')} className="flex items-center gap-1 hover:text-primary">
                    Eingangsnummer {pfeil('number')}
                  </button>
                  <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40" onMouseDown={(e) => handleResizeStart(e, 'number')} />
                </th>
                {shownColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`relative select-none px-4 py-1.5 font-medium text-foreground ${col.align === 'right' ? 'text-right' : 'text-left'} ${columnConfigMode ? 'cursor-grab' : ''} ${dragOverKey === col.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                    draggable={columnConfigMode}
                    onDragStart={columnConfigMode ? () => (dragKeyRef.current = col.key) : undefined}
                    onDragOver={
                      columnConfigMode
                        ? (e) => {
                            e.preventDefault()
                            setDragOverKey(col.key)
                          }
                        : undefined
                    }
                    onDrop={columnConfigMode ? () => handleDrop(col.key) : undefined}
                    onDragEnd={columnConfigMode ? () => setDragOverKey(null) : undefined}
                    style={colState.columnWidths[col.key] ? { width: colState.columnWidths[col.key] } : undefined}
                  >
                    <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                      {col.sortable ? (
                        <button type="button" onClick={() => handleSort(col.key)} className="flex items-center gap-1 hover:text-primary">
                          {col.label} {pfeil(col.key)}
                        </button>
                      ) : (
                        <span>{col.label}</span>
                      )}
                      {columnConfigMode && (
                        <button type="button" onClick={() => updateColState((prev) => ({ ...prev, columnKeys: prev.columnKeys.filter((k) => k !== col.key) }))} className="ml-1 text-muted-foreground hover:text-destructive" title="Spalte entfernen">
                          ✕
                        </button>
                      )}
                    </div>
                    <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40" onMouseDown={(e) => handleResizeStart(e, col.key)} />
                  </th>
                ))}
                {columnConfigMode && availableToAdd.length > 0 && (
                  <th className="px-2 py-1.5">
                    <select
                      className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground"
                      defaultValue=""
                      onChange={(e) => {
                        const key = e.target.value
                        if (!key) return
                        updateColState((prev) => ({ ...prev, columnKeys: [...prev.columnKeys, key] }))
                        e.target.value = ''
                      }}
                    >
                      <option value="">+ Spalte</option>
                      {availableToAdd.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </th>
                )}
                <th className="w-4" />
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    Laden…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {stapel ? 'Der Eingangsstapel ist leer. Zieh Rechnungen als PDF, Bild oder XML auf diese Seite.' : freigaben ? 'Nichts wartet auf deine Freigabe.' : 'Keine Eingangsrechnungen gefunden.'}
                  </td>
                </tr>
              )}
              {rows.map((z, idx) => (
                <tr
                  key={z.id}
                  onClick={() => navigate(`/incoming-invoices/${z.id}`)}
                  className={`cursor-pointer border-b border-border last:border-0 ${idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'} transition-colors hover:bg-accent/30`}
                >
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{z.number ?? <span className="text-muted-foreground">ohne Nummer</span>}</td>
                  {shownColumns.map((col) => (
                    <td key={col.key} className={`whitespace-nowrap px-4 py-1.5 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                      {renderCell(z, col)}
                    </td>
                  ))}
                  <td />
                </tr>
              ))}
              {rows.length > 0 && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  <td className="whitespace-nowrap px-4 py-1.5 text-muted-foreground">{meta ? `${meta.total} Einträge` : `${rows.length} Einträge`}</td>
                  {shownColumns.map((col) => (
                    <td key={col.key} className={`px-4 py-1.5 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                      {col.key === 'grossTotal' ? geldText(summe) : col.key === 'openAmount' ? geldText(summeOffen) : ''}
                    </td>
                  ))}
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {meta && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-sm text-muted-foreground">
            <span>
              Seite {meta.page} von {meta.totalPages} ({meta.total} Einträge)
            </span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40">
                ‹
              </button>
              <button type="button" disabled={page >= meta.totalPages || loading} onClick={() => setPage((p) => p + 1)} className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40">
                ›
              </button>
            </div>
          </div>
        )}
      </div>

      {offen && <OffeneRuecklaesse />}

      {anlegen && (
        <CreateInvoiceDialog
          onClose={() => setAnlegen(false)}
          onAngelegt={(r) => {
            setAnlegen(false)
            navigate(`/incoming-invoices/${r.id}`)
          }}
        />
      )}
    </div>
  )
}
